import { Button, Flex, Rte, Text, colors } from "@capioweb/ui-components/src"
import { assetBaseUrl, createMarkup } from "../../utils"
import styled from "styled-components"
import { NewsFeedItemProps } from "./"
import { LocalesContext } from "../../context"
import React from "react"
import { PUBLIC_PAGE_API_ENDPOINT } from "../../constants"

interface StyledCircleProps {
	topPosition: "top" | "bottom"
}

interface NewsFeedItemBlockProps extends NewsFeedItemProps {
	lastChild?: boolean
}

const StyledCircle = styled.div<StyledCircleProps>`
	width: 8px;
	height: 8px;
	border-radius: 50%;
	position: absolute;
	top: ${(props) => props.topPosition === "top" && "-5px"};
	bottom: ${(props) => props.topPosition === "bottom" && "-5px"};
	left: -5px;
	display: flex;
	background-color: ${colors.blue66};
`

const NewsFeedItemBlock: React.FC<NewsFeedItemBlockProps> = ({
	image,
	anchorId,
	editor,
	date,
	heading,
	preamble,
	body,
	pathname,
	lastChild,
}) => {
	const { translate, translationKeys } = React.useContext(LocalesContext)
	const [host, setHost] = React.useState<string>("")

	const hasImage = image?.media?.value

	React.useEffect(() => {
		if (window) setHost(window.location?.host)
	}, [window])

	const CaptionBlock = (
		<Flex w={1} backgroundColor="greyF3" p={{ _: 2, m: 3 }}>
			<Text fontStyle="italic" fontSize={1.75} lineHeight={2.5} mb={0}>
				{image?.imageCaption?.value}
			</Text>
		</Flex>
	)

	return (
		<>
			<Flex
				id={anchorId}
				flexDirection="column"
				position="relative"
				borderLeft
				borderColor="blue33"
				borderWidth="2px"
				pl={3}
			>
				<StyledCircle topPosition="top" />

				{/* Label / date */}
				<Flex mb={2}>
					<Flex pr={2} borderRight borderColor="blue66" mb={0}>
						<Text
							as="label"
							variant="headline-4"
							mb={0}
							textTransform="uppercase"
						>
							{editor}
						</Text>
					</Flex>

					<Text
						as="label"
						variant="paragraph-16"
						ml={2}
						mb={0}
						lineHeight={2.5}
					>
						{date}
					</Text>
				</Flex>
				{/* Heading */}
				<Text as="h2" variant="headline-1" mb={2}>
					{heading}
				</Text>
				{/* Image */}
				{hasImage && (
					<>
						<img
							src={assetBaseUrl() + image?.media?.value?.url}
							alt={image?.alternateText?.value}
							title={image?.imageTitle?.value}
							width="100%"
							height="100%"
							style={{ objectFit: "cover", aspectRatio: "3/2" }}
						/>
						{/* Image caption */}
						{image?.imageCaption?.value && CaptionBlock}
					</>
				)}
				{/* Wrapper for preamble and body */}
				<Flex w={1} flexDirection="column" rowGap={3} mt={hasImage ? 3 : 1}>
					{/* Preamble */}
					{preamble && (
						<Text variant="preamble" mb={0}>
							{preamble}
						</Text>
					)}
					{/* Body text */}
					{body && (
						<Rte>
							<div dangerouslySetInnerHTML={createMarkup(body)}></div>
						</Rte>
					)}
				</Flex>
				{/* Copy link button  */}
				<Flex mt={3}>
					<Button
						variant="ghost"
						iconPosition="left"
						icon="arrow-up-right"
						textColor="navy"
						onClick={() => {
							navigator.clipboard.writeText(`${host}${pathname}#${anchorId}`)
						}}
					>
						<Text mb={0} ml={1} variant="paragraph-14">
							{translate(translationKeys.copyLink)}
						</Text>
					</Button>
				</Flex>

				<StyledCircle topPosition="bottom" />
			</Flex>
			{!lastChild && (
				<Flex borderBottom pb={7} mb={7} mx={2} w="calc(100% - 32px)" />
			)}
		</>
	)
}

export { NewsFeedItemBlock }
